// Nexus Dex glyph system, original artwork generated procedurally per species.
//
// LEGAL: This file replaces all third-party sprite imagery (formerly PokeAPI).
// Glyphs are 100% original SVG, generated from the species id, types and a
// short letter mark derived from the species name. They never reproduce
// Game Freak / Nintendo / TPCi creative work.
//
// Visual concept:
//   - Rounded square plate with a type-coloured gradient band on the left
//   - Three-letter mark (first 3 letters of species name, uppercase)
//   - National Dex number underneath as a small chip
//   - Optional secondary type appears as a thin diagonal accent stripe
//
// All shapes are flat geometric primitives, monoline strokes, no
// resemblance to any TPCi mark.
//
// Distinct from a Pokéball: no white-and-red split sphere, no horizontal
// equator line, no centre button. The plate is a square, not a circle.

import { typesFor, TYPE_INFO, type PokeType } from "./types";
import { findPokemon } from "./pokemon";

// Cap letters at 3 to keep the plate readable at 36px and below.
function letterMarkFor(name: string): string {
  // Strip non-letters (e.g. "Mr. Mime", "Tapu Lele"), uppercase, take first 3.
  const cleaned = name.replace(/[^A-Za-z]/g, "");
  return cleaned.slice(0, 3).toUpperCase() || "DEX";
}

export interface GlyphOpts {
  shiny?: boolean;
  // hq, render at higher detail (used for detail sheets and battle hero)
  hq?: boolean;
  // grayscale, dim for "seen" or "missing" states
  desaturate?: boolean;
}

/**
 * Returns an SVG element string for a given national dex id.
 * Pure original artwork, no third-party imagery.
 */
export function nexusGlyphSvg(id: number, opts: GlyphOpts = {}): string {
  const species = findPokemon(id);
  const name = species?.name ?? `#${id}`;
  const types = typesFor(id);
  const primary = types[0] ?? "normal";
  const secondary = types[1];

  const primaryHex = TYPE_INFO[primary as PokeType]?.color ?? "#A8A878";
  const secondaryHex = secondary ? TYPE_INFO[secondary as PokeType]?.color : undefined;

  const mark = letterMarkFor(name);
  const dexLabel = id.toString().padStart(4, "0");

  // Shiny treatment, gold-plated edge and a small star
  const shiny = opts.shiny;
  const desat = opts.desaturate;

  const baseFill = shiny ? "#FFFCEF" : "#0F1320";
  const plateFill = shiny ? "#FFF7DC" : "#161B2E";
  const inkColor = shiny ? "#3B2E07" : "#F5F7FB";
  const subInkColor = shiny ? "#6B5414" : "#9CA3B8";
  const borderColor = shiny ? "#E0B848" : "#262C44";

  // Build a deterministic 64x64 SVG. ViewBox keeps everything crisp.
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" role="img" aria-label="${name} glyph">
  <defs>
    <linearGradient id="g${id}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${primaryHex}" stop-opacity="0.95"/>
      <stop offset="100%" stop-color="${primaryHex}" stop-opacity="0.55"/>
    </linearGradient>
    ${secondaryHex ? `
    <linearGradient id="g2${id}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${secondaryHex}" stop-opacity="0.85"/>
      <stop offset="100%" stop-color="${secondaryHex}" stop-opacity="0.35"/>
    </linearGradient>` : ""}
  </defs>

  <!-- Plate -->
  <rect x="2" y="2" width="60" height="60" rx="11" ry="11"
        fill="${plateFill}" stroke="${borderColor}" stroke-width="1.2"/>

  <!-- Primary type colour band, left edge -->
  <rect x="2" y="2" width="9" height="60" rx="4" ry="4" fill="url(#g${id})"/>

  ${secondary ? `
  <!-- Secondary type accent, diagonal stripe in lower right -->
  <path d="M 64 36 L 64 64 L 36 64 Z" fill="url(#g2${id})" opacity="0.55"/>` : ""}

  <!-- Three-letter mark, large -->
  <text x="36" y="34" text-anchor="middle"
        font-family="ui-sans-serif, system-ui, -apple-system, 'Inter', sans-serif"
        font-weight="800" font-size="16" letter-spacing="0.4"
        fill="${inkColor}">${mark}</text>

  <!-- Dex number chip -->
  <rect x="14" y="44" width="42" height="13" rx="3" ry="3"
        fill="${baseFill}" stroke="${borderColor}" stroke-width="0.7" opacity="0.92"/>
  <text x="35" y="53" text-anchor="middle"
        font-family="ui-monospace, SFMono-Regular, Menlo, monospace"
        font-weight="600" font-size="7.5" letter-spacing="0.6"
        fill="${subInkColor}">N°${dexLabel}</text>

  ${shiny ? `
  <!-- Shiny star -->
  <path d="M 53 9 L 55 13 L 59 13.5 L 56 16.5 L 56.7 20.5 L 53 18.5 L 49.3 20.5 L 50 16.5 L 47 13.5 L 51 13 Z"
        fill="#E0B848" stroke="#B8902A" stroke-width="0.5"/>` : ""}
</svg>`.trim();

  if (desat) {
    return svg.replace(
      "<defs>",
      `<defs><filter id="ds${id}"><feColorMatrix type="saturate" values="0.15"/></filter>`
    ).replace(
      `<rect x="2" y="2" width="60" height="60" rx="11"`,
      `<rect filter="url(#ds${id})" x="2" y="2" width="60" height="60" rx="11"`
    );
  }

  return svg;
}

/**
 * Returns a data: URI for the glyph so it can be used in <img src=...>.
 * Use this only when JSX inline-SVG is awkward (e.g. inside <img> for legacy
 * components). Prefer the <NexusGlyph/> React component below for new code.
 */
export function nexusGlyphDataUri(id: number, opts: GlyphOpts = {}): string {
  const svg = nexusGlyphSvg(id, opts);
  // Use encodeURIComponent so SVG content with quotes survives the data URI.
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
